import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Bell, Mail, MessageSquare, ToggleLeft, ToggleRight } from 'lucide-react';

interface ReminderConfig {
  id: string;
  name: string;
  trigger: string;
  daysOffset: number;
  channels: ('EMAIL' | 'SMS' | 'IN_APP')[];
  isActive: boolean;
  template: string;
}

const mockReminders: ReminderConfig[] = [
  {
    id: '1',
    name: 'Rappel 7 jours avant',
    trigger: 'BEFORE_DUE',
    daysOffset: -7,
    channels: ['EMAIL', 'IN_APP'],
    isActive: true,
    template: 'Votre échéance de {amount} arrive dans 7 jours...',
  },
  {
    id: '2',
    name: 'Rappel jour J',
    trigger: 'BEFORE_DUE',
    daysOffset: 0,
    channels: ['EMAIL', 'SMS', 'IN_APP'],
    isActive: true,
    template: 'Votre échéance de {amount} est due aujourd\'hui...',
  },
  {
    id: '3',
    name: 'Relance J+1',
    trigger: 'AFTER_DUE',
    daysOffset: 1,
    channels: ['EMAIL', 'IN_APP'],
    isActive: true,
    template: 'Votre paiement de {amount} est en retard...',
  },
  {
    id: '4',
    name: 'Relance J+15',
    trigger: 'OVERDUE_15',
    daysOffset: 15,
    channels: ['EMAIL', 'SMS'],
    isActive: true,
    template: 'URGENT: Votre compte présente un impayé de {amount}...',
  },
  {
    id: '5',
    name: 'Avertissement Suspension',
    trigger: 'SUSPENSION_WARNING',
    daysOffset: 25,
    channels: ['EMAIL', 'SMS', 'IN_APP'],
    isActive: true,
    template: 'DERNIER AVIS: Risque de suspension de compte...',
  },
];

const triggerLabels: Record<string, string> = {
  BEFORE_DUE: 'Avant échéance',
  AFTER_DUE: 'Après échéance',
  OVERDUE_15: '15 jours retard',
  OVERDUE_30: '30 jours retard',
  SUSPENSION_WARNING: 'Avert. suspension',
};

const triggerColors: Record<string, string> = {
  BEFORE_DUE: 'bg-blue-100 text-blue-700',
  AFTER_DUE: 'bg-amber-100 text-amber-700',
  OVERDUE_15: 'bg-orange-100 text-orange-700',
  OVERDUE_30: 'bg-red-100 text-red-700',
  SUSPENSION_WARNING: 'bg-red-100 text-red-700',
};

const channelIcons: Record<string, React.ReactNode> = {
  EMAIL: <Mail size={16} />,
  SMS: <MessageSquare size={16} />,
  IN_APP: <Bell size={16} />,
};

export const RemindersConfigPage: React.FC = () => {
  const [reminders, setReminders] = useState<ReminderConfig[]>(mockReminders);

  const toggleReminder = (id: string) => {
    setReminders(
      reminders.map((r) => (r.id === id ? { ...r, isActive: !r.isActive } : r))
    );
  };

  const formatDaysOffset = (days: number) => {
    if (days === 0) return 'Jour J';
    if (days < 0) return `J${days}`;
    return `J+${days}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramétrage Relances</h1>
          <p className="text-gray-500 mt-1">
            Configuration des relances automatiques de paiement
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Plus size={20} />
          Nouvelle Relance
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Relances Automatiques</h3>
            <p className="text-sm text-blue-700 mt-1">
              Les relances sont envoyées automatiquement selon le calendrier configuré.
              Les étudiants reçoivent les notifications sur les canaux sélectionnés.
            </p>
          </div>
        </div>
      </div>

      {/* Timeline View */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-900 mb-6">Chronologie des Relances</h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          <div className="space-y-6">
            {reminders
              .sort((a, b) => a.daysOffset - b.daysOffset)
              .map((reminder) => (
                <div key={reminder.id} className="relative flex gap-4">
                  {/* Timeline dot */}
                  <div
                    className={`relative z-10 h-16 w-16 rounded-full flex items-center justify-center text-sm font-bold ${
                      reminder.isActive
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                    }`}
                  >
                    {formatDaysOffset(reminder.daysOffset)}
                  </div>

                  {/* Card */}
                  <div
                    className={`flex-1 p-4 rounded-xl border ${
                      reminder.isActive
                        ? 'bg-white border-gray-200'
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3
                            className={`font-medium ${
                              reminder.isActive ? 'text-gray-900' : 'text-gray-400'
                            }`}
                          >
                            {reminder.name}
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              triggerColors[reminder.trigger]
                            }`}
                          >
                            {triggerLabels[reminder.trigger]}
                          </span>
                        </div>

                        <p
                          className={`text-sm mb-3 ${
                            reminder.isActive ? 'text-gray-600' : 'text-gray-400'
                          }`}
                        >
                          {reminder.template}
                        </p>

                        <div className="flex items-center gap-3">
                          {reminder.channels.map((channel) => (
                            <span
                              key={channel}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                reminder.isActive
                                  ? 'bg-gray-100 text-gray-600'
                                  : 'bg-gray-50 text-gray-400'
                              }`}
                            >
                              {channelIcons[channel]}
                              {channel}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleReminder(reminder.id)}
                          className={reminder.isActive ? 'text-green-600' : 'text-gray-400'}
                        >
                          {reminder.isActive ? (
                            <ToggleRight size={28} />
                          ) : (
                            <ToggleLeft size={28} />
                          )}
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors">
                          <Edit2 size={18} />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Emails envoyés (30j)</p>
              <p className="text-xl font-bold text-gray-900">234</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">SMS envoyés (30j)</p>
              <p className="text-xl font-bold text-gray-900">89</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Taux de recouvrement</p>
              <p className="text-xl font-bold text-gray-900">78%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
